<script setup>
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import Button from "@/shared/ui/button/Button.vue";

const props = defineProps({
  state: {
    type: String,
    default: "unchecked",
    validator: (v) =>
      ["unchecked", "triggering", "running", "done", "stale", "error"].includes(
        v
      ),
  },
  criticalCount: { type: Number, default: 0 },
  refusalRisk: { type: Number, default: 0 },
  lastCheckedAt: { type: String, default: "" },
  errorMessage: { type: String, default: "" },
  newDocsCount: { type: Number, default: 0 },
  removedDocsCount: { type: Number, default: 0 },
  progress: { type: Number, default: 0 },
  documentsCount: { type: Number, default: 0 },
  topIssues: { type: Array, default: () => [] },
  initialLoading: { type: Boolean, default: false },
});

const emit = defineEmits(["open", "start", "restart", "retry", "cancel"]);

const { t } = useI18n();

/* ---------- pluralization ---------- */
function pluralRu(n, [one, few, many]) {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
  return many;
}
const pluralIssues = (n) =>
  pluralRu(n, [t("замечание"), t("замечания"), t("замечаний")]);
const pluralDocs = (n) =>
  pluralRu(n, [t("документ"), t("документа"), t("документов")]);
const pluralChanges = (n) =>
  pluralRu(n, [t("изменение"), t("изменения"), t("изменений")]);

/* ---------- derived state ---------- */
const isLoading = computed(
  () => props.state === "triggering" || props.state === "running"
);
const hasIssues = computed(() => props.criticalCount > 0);
const isDone = computed(() => props.state === "done");
const isDoneClean = computed(() => isDone.value && !hasIssues.value);
const isDoneIssues = computed(() => isDone.value && hasIssues.value);

const themeKey = computed(() => {
  switch (props.state) {
    case "unchecked":
    case "triggering":
    case "running":
      return "violet";
    case "done":
      if (!hasIssues.value) return "green";
      return (props.refusalRisk || 0) < 20 ? "yellow" : "red";
    case "stale":
      return "amber";
    case "error":
      return "red";
    default:
      return "violet";
  }
});

const pillText = computed(() => {
  switch (props.state) {
    case "unchecked":
      return t("Готово к запуску");
    case "triggering":
      return t("Запускаем");
    case "running":
      return t("В работе");
    case "done":
      return hasIssues.value ? t("Высокий риск") : t("Успешно");
    case "stale":
      return t("Устарело");
    case "error":
      return t("Ошибка");
    default:
      return "";
  }
});

const leftDisplay = computed(() => {
  if (props.state === "running") {
    return {
      kind: "number",
      num: Math.round(props.progress || 0),
      unit: "%",
      label: t("выполнено"),
      icon: null,
    };
  }
  if (props.state === "triggering") {
    return {
      kind: "spinner",
      label: t("Подготовка"),
      icon: null,
    };
  }
  if (props.state === "unchecked") {
    return {
      kind: "icon",
      icon: "huge-ai",
      label: props.documentsCount
        ? `${props.documentsCount} ${pluralDocs(props.documentsCount)}`
        : t("К проверке"),
    };
  }
  if (isDoneClean.value) {
    return {
      kind: "icon",
      icon: "sli-check-circle",
      iconClass: "text-green-500 fill-none",
      label: t("Проверено"),
    };
  }
  if (isDoneIssues.value) {
    return {
      kind: "number",
      num: Math.round(props.refusalRisk || 0),
      unit: "%",
      label: t("риск отказа по заключению AI"),
      icon: null,
    };
  }
  if (props.state === "stale") {
    const added = props.newDocsCount;
    const removed = props.removedDocsCount;

    // Только добавили / только удалили → знак со счётом
    if (added > 0 && removed === 0) {
      return {
        kind: "number",
        num: `+${added}`,
        unit: pluralDocs(added),
        label: t("с момента последней проверки"),
        icon: null,
      };
    }
    if (removed > 0 && added === 0) {
      return {
        kind: "number",
        num: `−${removed}`,
        unit: pluralDocs(removed),
        label: t("с момента последней проверки"),
        icon: null,
      };
    }
    // И добавили, и удалили → суммарное число изменений без знака
    if (added > 0 && removed > 0) {
      const total = added + removed;
      return {
        kind: "number",
        num: total,
        unit: pluralChanges(total),
        label: t("с момента последней проверки"),
        icon: null,
      };
    }
    // Fallback: needs_recheck=true от бэка, но локально diff не виден
    return {
      kind: "icon",
      icon: "huge-warning",
      iconClass: "text-orange-500 fill-none",
      label: t("Есть изменения"),
    };
  }
  if (props.state === "error") {
    return {
      kind: "icon",
      icon: "sli-danger",
      label: t("Сервис недоступен"),
    };
  }
  return { kind: "icon", icon: "huge-ai", label: "" };
});

// Заголовок постоянный — состояние передаётся через pill (слева) + lead-описание.
const titleText = computed(() => t("Проверка AI документов"));

const leadText = computed(() => {
  switch (props.state) {
    case "unchecked":
      if (props.documentsCount > 0) {
        return t(
          "Проверим {n} {plural} на ошибки и несоответствия. Обычно занимает около минуты.",
          { n: props.documentsCount, plural: pluralDocs(props.documentsCount) }
        );
      }
      return t("AI проверит пакет документов перед подписанием.");
    case "triggering":
      return t("Отправляем документы в сервис.");
    case "running":
      return t(
        "Проверка идёт в фоне — можно продолжать работу. Уведомление придёт, когда заключение будет готово."
      );
    case "done": {
      const datePart = props.lastCheckedAt
        ? `${t("Заключение от")} ${props.lastCheckedAt}. `
        : "";
      if (hasIssues.value) {
        const issuesPhrase = `${t("AI обнаружил")} ${
          props.criticalCount
        } ${pluralIssues(props.criticalCount)}`;
        return `${issuesPhrase}${datePart ? ` · ${datePart}` : ". "}${t(
          "Требуется проверка документов."
        )}`;
      }
      return `${datePart}${t("AI не нашёл проблем в документах.")}`;
    }
    case "stale":
      return props.lastCheckedAt
        ? t(
            "Анализ от {date} сделан до изменений в пакете документов. Перезапустите проверку."
          ).replace("{date}", props.lastCheckedAt)
        : t("Список документов изменился — перезапустите проверку.");
    case "error":
      return (
        (props.errorMessage
          ? props.errorMessage + " "
          : t("AI-сервис временно недоступен. ") + " ") +
        t("Документы и предыдущее заключение сохранены.")
      );
    default:
      return "";
  }
});

/* ---------- 2-step progress for running ---------- */
const steps = computed(() => {
  if (props.state !== "running" && props.state !== "triggering") return [];
  const p = props.progress || 0;
  const docs = props.documentsCount;
  const stepStatus = (start, end) => {
    if (p >= end) return "done";
    if (p >= start) return "active";
    return "pending";
  };
  return [
    {
      label: docs
        ? `${t("Сбор пакета")} · ${docs} ${pluralDocs(docs)}`
        : t("Сбор пакета"),
      status: props.state === "triggering" ? "active" : stepStatus(0, 50),
    },
    {
      label: t("Формирование заключения"),
      status: props.state === "triggering" ? "pending" : stepStatus(50, 100),
    },
  ];
});

/* ---------- actions ---------- */
const primaryAction = computed(() => {
  switch (props.state) {
    case "unchecked":
      return { label: t("Запустить проверку"), icon: "huge-ai", emit: "start" };
    case "done":
      return {
        label: hasIssues.value
          ? t("Посмотреть замечания")
          : t("Посмотреть результат"),
        icon: "huge-eye",
        emit: "open",
      };
    case "stale":
      return {
        label: t("Перепроверить · ~1 мин"),
        icon: "huge-ai",
        emit: "restart",
      };
    case "error":
      return { label: t("Повторить"), icon: "huge-ai", emit: "retry" };
    default:
      return null;
  }
});

const secondaryAction = computed(() => {
  if (props.state === "stale") {
    return {
      label: t("Прошлое заключение"),
      icon: "huge-eye",
      emit: "open",
    };
  }
  if (props.state === "done") {
    return {
      label: t("Перепроверить"),
      icon: "sli-refresh",
      emit: "restart",
    };
  }
  if (props.state === "running" || props.state === "triggering") {
    return {
      label: t("Отменить"),
      icon: "huge-close",
      emit: "cancel",
    };
  }
  // error: вторичной кнопки нет — primary "Повторить" достаточно
  return null;
});

function onPrimary() {
  if (!primaryAction.value) return;
  emit(primaryAction.value.emit);
}
function onSecondary() {
  if (!secondaryAction.value) return;
  emit(secondaryAction.value.emit);
}
</script>

<template>
  <!-- Skeleton placeholder во время первичного GET — не мерцать unchecked → done -->
  <div v-if="initialLoading" class="cr cr--skeleton">
    <div class="cr__left cr__left--violet">
      <div class="cr__skel cr__skel--pill" />
      <div class="cr__display-wrap">
        <div class="cr__skel cr__skel--num" />
        <div class="cr__skel cr__skel--label" />
      </div>
    </div>
    <div class="cr__right">
      <div class="cr__skel cr__skel--title" />
      <div class="cr__skel cr__skel--lead" />
      <div class="cr__skel cr__skel--lead cr__skel--lead-short" />
      <div class="cr__actions">
        <div class="cr__skel cr__skel--btn" />
      </div>
    </div>
  </div>

  <div v-else :class="['cr', `cr--${themeKey}`]">
    <!-- LEFT: hero column with gradient + big display -->
    <div :class="['cr__left', `cr__left--${themeKey}`]">
      <div class="cr__pill-row">
        <div :class="['pill', `pill--${themeKey}`]">
          <i class="pill__dot" />
          {{ pillText }}
        </div>
      </div>

      <div class="cr__display-wrap">
        <div v-if="leftDisplay.kind === 'number'" class="cr__display">
          <strong :class="['cr__display-num', `cr__display-num--${themeKey}`]">
            {{ leftDisplay.num }}
          </strong>
          <em v-if="leftDisplay.unit" class="cr__display-unit">
            {{ leftDisplay.unit }}
          </em>
        </div>

        <div v-else-if="leftDisplay.kind === 'spinner'" class="cr__display">
          <div class="spinner spinner--lg" />
        </div>

        <div v-else-if="leftDisplay.kind === 'icon'" class="cr__display-icon">
          <svg-icon
            :name="leftDisplay.icon"
            :class="['w-5 h-5', leftDisplay.iconClass || 'fill-none']"
          />
        </div>

        <div v-if="leftDisplay.label" class="cr__display-label">
          {{ leftDisplay.label }}
        </div>
      </div>
    </div>

    <!-- RIGHT: title + lead + state-specific content + actions -->
    <div class="cr__right">
      <div class="cr__title">{{ titleText }}</div>
      <p class="cr__lead">{{ leadText }}</p>

      <!-- Step indicator for running/triggering -->
      <div v-if="steps.length" class="cr__steps">
        <div
          v-for="(step, i) in steps"
          :key="i"
          :class="['cr__step', `cr__step--${step.status}`]"
        >
          <svg-icon
            v-if="step.status === 'done'"
            name="sli-check-circle"
            class="w-3.5 h-3.5 fill-none text-green-500 shrink-0"
          />
          <i v-else class="cr__step-dot" />
          <p class="cr__step-label">{{ step.label }}</p>
        </div>
      </div>

      <!-- Findings list for done with issues -->
      <ul
        v-if="state === 'done' && hasIssues && topIssues.length"
        class="cr__list"
      >
        <li v-for="(issue, i) in topIssues" :key="i">
          <svg-icon
            name="sli-check-circle"
            :class="[
              'w-3.5 h-3.5 shrink-0 fill-none',
              issue.severity === 'bad' ? 'text-red-600' : 'text-yellow-600',
            ]"
          />
          <p class="cr__issue-text">{{ issue.title }}</p>
        </li>
      </ul>

      <!-- Actions -->
      <div v-if="primaryAction || secondaryAction" class="cr__actions">
        <Button
          v-if="secondaryAction"
          variant="outline"
          class="gap-2"
          @click="onSecondary"
        >
          <svg-icon
            :name="secondaryAction.icon"
            class="w-3.5 h-3.5 fill-none"
          />
          {{ secondaryAction.label }}
        </Button>
        <Button
          v-if="primaryAction"
          variant="default"
          class="gap-2"
          :disabled="isLoading"
          @click="onPrimary"
        >
          <svg-icon :name="primaryAction.icon" class="w-3.5 h-3.5" />
          {{ primaryAction.label }}
        </Button>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
/* ============================================================
   Variant C — Premium hero with split layout
   Адаптация из info-block/project/styles.css (.cr*)
   ============================================================ */
.cr {
  width: 100%;
  border-radius: var(--radius);
  background: hsl(var(--card));
  color: hsl(var(--card-foreground));
  border: 1px solid hsl(var(--border));
  overflow: hidden;
  display: grid;
  grid-template-columns: 180px 1fr;
  box-shadow: 0 1px 2px rgb(0 0 0 / 0.04);

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
}

.cr__left {
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 12px;
  border-right: 1px solid hsl(var(--border));
  position: relative;
  min-height: 132px;

  @media (max-width: 640px) {
    border-right: 0;
    border-bottom: 1px solid hsl(var(--border));
    min-height: auto;
    padding: 12px 14px;
  }
}

.cr__left--violet {
  background: linear-gradient(
    160deg,
    rgb(var(--color-violet-50)) 0%,
    hsl(var(--card)) 80%
  );
}
.cr__left--amber {
  background: linear-gradient(
    160deg,
    rgb(var(--color-orange-50)) 0%,
    hsl(var(--card)) 80%
  );
}
.cr__left--red {
  background: linear-gradient(
    160deg,
    rgb(var(--color-red-50)) 0%,
    hsl(var(--card)) 80%
  );
}
.cr__left--green {
  background: linear-gradient(
    160deg,
    rgb(var(--color-green-50)) 0%,
    hsl(var(--card)) 80%
  );
}
.cr__left--yellow {
  background: linear-gradient(
    160deg,
    rgb(var(--color-yellow-50)) 0%,
    hsl(var(--card)) 80%
  );
}

.cr__pill-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.cr__display-wrap {
  display: flex;
  flex-direction: column;
}

.cr__display {
  display: flex;
  align-items: baseline;
  gap: 6px;
}
.cr__display-icon {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  color: rgb(var(--color-indigo-500));
}
.cr--amber .cr__display-icon {
  color: rgb(var(--color-orange-700));
}
.cr--red .cr__display-icon {
  color: rgb(var(--color-red-500));
}
.cr--green .cr__display-icon {
  color: rgb(var(--color-green-500));
}
.cr--yellow .cr__display-icon {
  color: rgb(var(--color-yellow-600));
}
.cr__display-num {
  font-size: 30px;
  line-height: 1;
  font-weight: 600;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
  color: hsl(var(--foreground));
  font-style: normal;
}
.cr__display-num--red {
  color: rgb(var(--color-red-600));
}
.cr__display-num--violet {
  color: rgb(var(--color-violet-600));
}
.cr__display-num--amber {
  color: rgb(var(--color-orange-700));
}
.cr__display-num--green {
  color: rgb(var(--color-green-600));
}
.cr__display-num--yellow {
  color: rgb(var(--color-yellow-700));
}
.cr__display-unit {
  font-size: 12px;
  font-weight: 500;
  color: hsl(var(--muted-foreground));
  font-style: normal;
}
.cr__display-label {
  margin-top: 4px;
  font-size: 11px;
  color: hsl(var(--muted-foreground));
  line-height: 14px;
}

/* ---------- Pill ---------- */
.pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 7px 2px 6px;
  border-radius: 999px;
  font-size: 10.5px;
  line-height: 1;
  font-weight: 500;
  letter-spacing: 0.01em;
}
.pill__dot {
  width: 5px;
  height: 5px;
  border-radius: 999px;
}
.pill--violet {
  background: rgb(var(--color-violet-100));
  color: rgb(var(--color-violet-700));
}
.pill--violet .pill__dot {
  background: rgb(var(--color-violet-500));
}
.pill--amber {
  background: rgb(var(--color-orange-100));
  color: rgb(var(--color-orange-700));
}
.pill--amber .pill__dot {
  background: rgb(var(--color-orange-500));
}
.pill--red {
  background: rgb(var(--color-red-100));
  color: rgb(var(--color-red-600));
}
.pill--red .pill__dot {
  background: rgb(var(--color-red-500));
}
.pill--green {
  background: rgb(var(--color-green-100));
  color: rgb(var(--color-green-700));
}
.pill--green .pill__dot {
  background: rgb(var(--color-green-500));
}
.pill--yellow {
  background: rgb(var(--color-yellow-100));
  color: rgb(var(--color-yellow-700));
}
.pill--yellow .pill__dot {
  background: rgb(var(--color-yellow-500));
}

/* ---------- Right column ---------- */
.cr__right {
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  min-width: 0;

  @media (max-width: 640px) {
    padding: 12px 14px;
  }
}
.cr__title {
  font-size: 14px;
  line-height: 20px;
  font-weight: 600;
  color: hsl(var(--foreground));
  letter-spacing: -0.005em;
}
.cr__lead {
  font-size: 13px;
  line-height: 17px;
  color: hsl(var(--muted-foreground));
  margin: 0;
}

/* ---------- Steps ---------- */
.cr__steps {
  margin-top: 4px;
  display: flex;
  flex-direction: column;
  gap: 7px;
}
.cr__step {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: hsl(var(--muted-foreground));
}
.cr__step-dot {
  width: 12px;
  height: 12px;
  border-radius: 999px;
  border: 1.5px solid rgb(var(--color-gray-300));
  background: hsl(var(--card));
  display: grid;
  place-items: center;
  flex-shrink: 0;
  font-style: normal;
}
.cr__step-label {
  margin: 0;
  line-height: 1.4;
}
.cr__issue-text {
  margin: 0;
  font-size: 12px;
  line-height: 1.4;
}
.cr__step--done {
  color: hsl(var(--foreground));
}
.cr__step--active .cr__step-dot {
  border-color: rgb(var(--color-violet-500));
  background: rgb(var(--color-violet-50));
  position: relative;
}
.cr__step--active .cr__step-dot::after {
  content: "";
  position: absolute;
  inset: -3px;
  border-radius: 999px;
  border: 2px solid rgb(var(--color-violet-300));
  animation: ai-banner-pulse-ring 1.6s ease-out infinite;
}
.cr__step--active {
  color: hsl(var(--foreground));
  font-weight: 500;
}

@keyframes ai-banner-pulse-ring {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  100% {
    transform: scale(1.8);
    opacity: 0;
  }
}

/* ---------- List (diff / findings) ---------- */
.cr__list {
  margin: 4px 0 4px;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.cr__list li {
  display: flex;
  align-items: start;
  gap: 7px;
  font-size: 12px;
  color: rgb(51 65 85);
}
.cr__list-more {
  font-size: 11px;
  color: rgb(100 116 139);
  font-style: italic;
}
.cr__diff-name {
  color: rgb(15 23 42);
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}
.pill__dot {
  font-style: normal;
}

/* ---------- Actions ---------- */
.cr__actions {
  margin-top: auto;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

/* ---------- Skeleton (initial load) ---------- */
.cr--skeleton .cr__left {
  background: hsl(var(--muted));
}
.cr__skel {
  background: hsl(var(--muted));
  border-radius: 4px;
  animation: ai-banner-skel 1.4s ease-in-out infinite;
}
.cr__skel--pill {
  width: 80px;
  height: 18px;
  border-radius: 999px;
}
.cr__skel--num {
  width: 60%;
  height: 30px;
  margin-bottom: 6px;
}
.cr__skel--label {
  width: 80%;
  height: 11px;
}
.cr__skel--title {
  width: 50%;
  height: 14px;
  margin-bottom: 4px;
}
.cr__skel--lead {
  width: 100%;
  height: 12px;
}
.cr__skel--lead-short {
  width: 70%;
}
.cr__skel--btn {
  width: 140px;
  height: 32px;
  border-radius: var(--radius);
}
@keyframes ai-banner-skel {
  0%,
  100% {
    opacity: 0.5;
  }
  50% {
    opacity: 0.8;
  }
}

/* ---------- Spinner ---------- */
.spinner {
  width: 12px;
  height: 12px;
  border-radius: 999px;
  border: 2px solid rgb(var(--color-violet-100));
  border-top-color: rgb(var(--color-violet-500));
  animation: ai-banner-spin 0.9s linear infinite;
}
.spinner--lg {
  width: 24px;
  height: 24px;
  border-width: 2.5px;
}
@keyframes ai-banner-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
